'use client';

import { Box, VStack, HStack } from '@terra/ui-styled-system';
import {
  ArtifactsProvider,
  BodyDefault,
  HeadingItem,
  LabelDefault,
  Skeleton,
  TraceView,
  EntityResolverProvider,
  IconButton,
  Tooltip,
  TraceSimplifiedView,
  Button,
  Menu,
  toasterApi,
  ConfirmationDialog,
  mapTraceMetadataFromSmokeTestHistory,
  type TraceExpandedSections,
  type TraceSimplifiedViewHandle,
  type TraceViewHandle,
  CopyIdButton,
  ArchivedLifecycleBadge,
  isArchivedTracePreview,
} from '@terra/ui-design-system';
import { ArchivedActionGate } from '../common/ArchivedActionGate';
import { useMentions } from '../../hooks/useMentions';
import { useChatStore } from '../../store/chatStore';
import type { SmokeTestHistoryResponseDto } from '@terra/shared-contracts-smoke-test-history';
import type { Har } from 'har-format';
import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
  type MouseEvent,
} from 'react';
import { useTranslations } from 'next-intl';
import { Logger } from '../../electron/logger';
import { useTraceScrollPreservation } from '../../store/tabViewStateStore';
import { useTraceEntityResolvers } from '../../hooks/useTraceEntityResolvers';
import {
  CheckListIcon,
  Download01Icon,
  ViewIcon,
  FileScriptIcon,
  Message01Icon,
  PlayIcon,
  RefreshIcon,
  Route01Icon,
  ShieldBanIcon,
  StopIcon,
  Delete02Icon,
} from '@terra/ui-icons';
import type { OpenMainContentTabInput } from '../../store/layoutStore';
import { useMainContentTabs } from '../../store/layoutStore';
import { useTraceViewSettingsStore } from '../../store/traceViewSettingsStore';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useExecutionsStore } from '../../store/executionsStore';
import { GlobalMenuItemsTypeEnum } from '../../store/globalContextMenuStore';
import { useGlobalContextMenu } from '../../hooks/useGlobalContextMenu';
import { ExecutionTraceTabs } from '../expected-response-observations/ExecutionTraceTabs';
import { useTraceRunActions } from './useTraceRunActions';
import { buildCreateFindingChatPrompt } from './build-create-finding-chat-prompt';

const logger = new Logger('TraceContent');

type TraceContentProps = {
  applicationId: string;
  threadId: string;
  history: SmokeTestHistoryResponseDto | undefined;
  isLoading: boolean;
  error: Error | null;
  agentName?: string;
  executionId?: string;
  expandedSections?: TraceExpandedSections;
  onExpandedSectionsChange?: (sections: TraceExpandedSections) => void;
  executionTab?: string;
  onExecutionTabChange?: (tab: string) => void;
  onAbortSuccess?: () => Promise<void> | void;
  onResumeSuccess?: () => Promise<void> | void;
  onDeleteSuccess?: () => Promise<void> | void;
  onStepVisibilityChange?: (input: {
    stepKey: string;
    visible: boolean;
  }) => void;
};

export function TraceContent({
  applicationId,
  threadId,
  history,
  isLoading,
  error,
  agentName,
  executionId,
  expandedSections,
  onExpandedSectionsChange,
  executionTab: executionTabProp,
  onExecutionTabChange,
  onAbortSuccess,
  onResumeSuccess,
  onDeleteSuccess,
  onStepVisibilityChange,
}: TraceContentProps) {
  const [isExportingHars, setIsExportingHars] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [localExecutionTab, setLocalExecutionTab] = useState('overview');
  const executionTab = executionTabProp ?? localExecutionTab;
  const setExecutionTab = onExecutionTabChange ?? setLocalExecutionTab;
  const hasSignal = Boolean(history?.signal && history.signal.id);
  const preferDetailedView = useTraceViewSettingsStore(
    (state) => state.showDetailedView,
  );
  const setShowDetailedView = useTraceViewSettingsStore(
    (state) => state.setShowDetailedView,
  );
  const hasSkipAttackReason = Boolean(history?.metadata?.skipAttackReason);
  const showDetailedView = preferDetailedView && !hasSkipAttackReason;
  const t = useTranslations('traceContent');
  const tExecutions = useTranslations('designSystem.executions');
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const isArchived = isArchivedTracePreview(history);
  const archivedActionTooltip = tLifecycle('archivedExecutionActionTooltip');

  useEffect(() => {
    if (!error) {
      return;
    }

    logger.warn('Failed to load execution trace', {
      threadId,
      executionId,
      error: error instanceof Error ? error.message : String(error),
    });
  }, [error, executionId, threadId]);

  const { addMention } = useMentions();
  const appendTextToInput = useChatStore((state) => state.appendTextToInput);
  const { navigateInActiveTab, closeTab } = useMainContentTabs();
  const { openContextMenu } = useGlobalContextMenu();
  const removeNeoAttackByThreadId = useExecutionsStore(
    (state) => state.removeNeoAttackByThreadId,
  );
  const simplifiedViewRef = useRef<TraceSimplifiedViewHandle>(null);
  const detailedViewRef = useRef<TraceViewHandle>(null);

  const isOverviewTabActive = executionTab === 'overview';

  useTraceScrollPreservation({
    threadId,
    isLoading,
    isActive: isOverviewTabActive && !showDetailedView,
    viewRef: simplifiedViewRef,
    stateKey: 'scrollPosition',
  });
  useTraceScrollPreservation({
    threadId,
    isLoading,
    isActive: isOverviewTabActive && showDetailedView,
    viewRef: detailedViewRef,
    stateKey: 'detailedScrollPosition',
  });
  const traceMetadata = useMemo(
    () => mapTraceMetadataFromSmokeTestHistory(history?.metadata),
    [history?.metadata],
  );
  const isAttackSkipped = Boolean(traceMetadata?.skipAttackReason);
  const handleNavigateToToolCall = useCallback(() => {
    // The audit log links reference tool calls surfaced in the execution
    // overview. Switch back to the detailed overview so the referenced tool
    // call is visible. Precise scroll-to-tool-call is not available on the
    // current TraceViewHandle, so we only restore the detailed overview view.
    setShowDetailedView(true);
    setExecutionTab('overview');
  }, [setShowDetailedView, setExecutionTab]);
  const executionStatus = useMemo(():
    | 'running'
    | 'completed'
    | 'error'
    | 'aborted' => {
    if (history?.metadata?.skipAttackReason) {
      return 'aborted';
    }

    if (history?.status) {
      switch (history.status) {
        case 'RUNNING':
          return 'running';
        case 'COMPLETED':
          return 'completed';
        case 'ABORTED':
          return 'aborted';
        case 'FAILED':
          return 'error';
        case 'PAUSED':
          return 'aborted';
        default:
          break;
      }
    }

    if (history?.plan?.some((p) => p.status === 'in_progress')) {
      return 'running';
    }

    return 'completed';
  }, [history?.status, history?.plan, history?.metadata?.skipAttackReason]);
  const {
    signalId,
    canAbort,
    canResume,
    isAborting,
    isRerunning,
    isResuming,
    handleAbort,
    handleRerun,
    handleResume,
  } = useTraceRunActions({
    applicationId,
    threadId,
    agentName,
    executionId,
    history,
    onAbortSuccess,
    onResumeSuccess,
  });
  const isBlockedByGuardrails = useMemo(() => {
    return (
      history?.metadata?.skipAttackReasonType === 'signal_blocked_by_guardrails'
    );
  }, [history?.metadata?.skipAttackReasonType]);
  const entityResolvers = useTraceEntityResolvers(applicationId);
  const handleExport = useCallback(() => {
    if (!history) return;
    const exportData = {
      threadId,
      signalId: history.signalId,
      signal: history.signal
        ? {
            id: history.signal.id,
            description: history.signal.description,
            analysis: history.signal.analysis,
            category: history.signal.category,
          }
        : null,
      history,
      exportedAt: new Date().toISOString(),
    };
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    a.href = url;
    a.download = `smoke-test-${threadId}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [threadId, history]);
  const handleExportAllHars = useCallback(async () => {
    setIsExportingHars(true);

    try {
      const response =
        await terraProxyApiClient.getToolHarsByThreadId(threadId);
      const hars = response?.hars ?? [];

      if (hars.length === 0) {
        console.warn('No HAR data found for this execution');

        return;
      }

      const harExport: Har = {
        log: {
          version: '1.2',
          creator: {
            name: 'Terra Security Platform',
            version: '1.0',
          },
          pages: [],
          entries: hars.map((har) => ({
            ...har.harEntry,
            cache: har.harEntry.cache ?? {},
            timings: {
              send: 0,
              ...har.harEntry.timings,
            },
            _toolCallId: har.toolCallId,
            _toolName: har.toolName,
            _toolType: har.toolType,
          })),
        },
      };
      const blob = new Blob([JSON.stringify(harExport, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');

      a.href = url;
      a.download = `smoke-test-hars-${threadId}-${Date.now()}.har`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export HAR data:', error);
    } finally {
      setIsExportingHars(false);
    }
  }, [threadId]);
  const buildTraceMention = useCallback(() => {
    if (!threadId) {
      return null;
    }

    const label = history?.signal?.description
      ? `Execution: ${history.signal.description.slice(0, 50)}${history.signal.description.length > 50 ? '...' : ''}`
      : `Execution: ${threadId.slice(0, 20)}...`;

    return {
      id: `executions-${threadId}`,
      label,
      icon: <Route01Icon size={12} />,
      type: 'child' as const,
      mentionEntity: {
        entityType: 'Trace' as const,
        entityId: threadId,
        label,
        extraData: {
          threadId,
          agentName,
          signalId: history?.signal?.id,
          signalDescription: history?.signal?.description,
          metadata: traceMetadata,
        },
      },
    };
  }, [threadId, history, agentName, traceMetadata]);
  const handleInvestigateInChat = useCallback(() => {
    const mention = buildTraceMention();

    if (!mention) {
      return;
    }

    setTimeout(() => {
      appendTextToInput(t('investigateChatPrompt'));
      addMention(mention);
    }, 0);
  }, [buildTraceMention, addMention, appendTextToInput, t]);
  const handleCreateFindingInChat = useCallback(() => {
    const mention = buildTraceMention();

    if (!mention) {
      return;
    }

    setTimeout(() => {
      appendTextToInput(
        buildCreateFindingChatPrompt({
          leadIn: t('createFindingChatPrompt'),
          instruction: t('createFindingChatIdsInstruction'),
          traceThreadId: threadId,
          postAttackTraceId: history?.postAttackTraceId,
          signalId: history?.signal?.id ?? history?.signalId,
        }),
      );
      addMention(mention);
    }, 0);
  }, [buildTraceMention, addMention, appendTextToInput, t, threadId, history]);
  const handleViewFullSignal = useCallback(
    (event?: MouseEvent<HTMLElement>) => {
      const isContextMenu = event?.type === 'contextmenu';
      const signalId = history?.signal?.id;

      if (!signalId) return;
      const title = history?.signal?.description || 'Signal Details';
      const tabData: OpenMainContentTabInput = {
        id: `signal-${signalId}`,
        title,
        type: 'signal',
        data: { signalId },
      };

      isContextMenu
        ? openContextMenu({
            event,
            items: [
              { type: GlobalMenuItemsTypeEnum.OPEN_IN_NEW_TAB, tab: tabData },
            ],
          })
        : navigateInActiveTab(tabData);
    },
    [
      history?.signal?.description,
      history?.signal?.id,
      navigateInActiveTab,
      openContextMenu,
    ],
  );
  const handleDeleteClick = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);
  const handleConfirmDelete = useCallback(async () => {
    if (!applicationId) return;
    setIsDeleting(true);

    try {
      await terraProxyApiClient.deleteTrace({
        applicationId,
        threadId,
      });
      removeNeoAttackByThreadId(threadId);
      await onDeleteSuccess?.();
      toasterApi.success({
        title: 'Execution deleted',
        description: 'The execution has been deleted successfully.',
      });
      setIsDeleteDialogOpen(false);
      closeTab(`executions-${threadId}`);
    } catch (deleteError) {
      console.error('Failed to delete trace:', deleteError);
      toasterApi.error({
        title: 'Failed to delete execution',
        description: 'There was an error deleting the execution.',
      });
    } finally {
      setIsDeleting(false);
    }
  }, [
    applicationId,
    closeTab,
    onDeleteSuccess,
    removeNeoAttackByThreadId,
    threadId,
  ]);

  if (isLoading) {
    return (
      <Box h="full" w="full" p="6">
        <VStack gap="4" alignItems="flex-start" w="full">
          <Skeleton h={8} w={50} />
          <Skeleton h={6} w={75} />
          <Skeleton h={100} w="full" />
        </VStack>
      </Box>
    );
  }

  if (error || !threadId || !history) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack gap="4" textAlign="center">
          <HeadingItem>{t('notFoundTitle')}</HeadingItem>
          <BodyDefault color="fg.muted">{t('notFoundDescription')}</BodyDefault>
        </VStack>
      </Box>
    );
  }

  const title =
    history.signal?.description ||
    history.signal?.analysis ||
    tExecutions('execution');

  return (
    <Box h="full" w="full" overflow="hidden">
      <VStack gap="0" h="full" alignItems="stretch">
        {/* Header */}
        <Box
          px="4"
          py="3"
          borderBottomWidth="1px"
          borderColor="border.subtle"
          bg="bg.default"
        >
          <HStack gap="3" alignItems="center" w="full">
            <HStack gap="2" flex="1" minW={0} alignItems="center">
              <HeadingItem truncate title={title}>
                {title}
              </HeadingItem>
              {isArchived ? (
                <ArchivedLifecycleBadge lifecycle={{ state: 'ARCHIVED' }} />
              ) : null}
            </HStack>

            {/* Action Buttons */}
            {threadId && (
              <CopyIdButton
                id={threadId}
                entityName={tExecutions('execution')}
              />
            )}
            <Tooltip title="Export Execution">
              <IconButton
                variant="ghost"
                size="xs"
                onClick={handleExport}
                aria-label="Export execution"
              >
                <Download01Icon size={14} />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                isExportingHars
                  ? 'Exporting...'
                  : 'Export All HTTP Requests (HAR)'
              }
            >
              <IconButton
                variant="ghost"
                size="xs"
                onClick={handleExportAllHars}
                aria-label="Export all HARs"
                disabled={isExportingHars}
              >
                <FileScriptIcon size={14} />
              </IconButton>
            </Tooltip>

            {/* Abort Button - only show if not in terminal state */}
            {canAbort && (
              <ArchivedActionGate
                isArchived={isArchived}
                tooltip={archivedActionTooltip}
              >
                <Tooltip title="Abort execution">
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={handleAbort}
                    aria-label="Abort execution"
                    disabled={isAborting}
                  >
                    <StopIcon size={14} />
                  </IconButton>
                </Tooltip>
              </ArchivedActionGate>
            )}

            {/* Resume Button - only show if aborted */}
            {canResume && (
              <ArchivedActionGate
                isArchived={isArchived}
                tooltip={archivedActionTooltip}
              >
                <Tooltip title="Resume execution">
                  <IconButton
                    variant="ghost"
                    size="xs"
                    onClick={handleResume}
                    aria-label="Resume execution"
                    disabled={isResuming}
                  >
                    <PlayIcon size={14} />
                  </IconButton>
                </Tooltip>
              </ArchivedActionGate>
            )}

            {/* Rerun Menu - only show when blocked by guardrails */}
            {isBlockedByGuardrails && signalId && (
              <ArchivedActionGate
                isArchived={isArchived}
                tooltip={archivedActionTooltip}
              >
                <Menu.Root
                  positioning={{
                    placement: 'bottom-end',
                    strategy: 'fixed',
                  }}
                >
                  <Menu.Trigger asChild>
                    <IconButton
                      variant="ghost"
                      size="xs"
                      aria-label={t('rerunOptions')}
                      disabled={isRerunning || isArchived}
                    >
                      <RefreshIcon
                        size={14}
                        className={isRerunning ? 'animate-spin' : ''}
                      />
                    </IconButton>
                  </Menu.Trigger>
                  <Menu.Positioner>
                    <Menu.Content minW="160px" py="1">
                      <Menu.ItemGroup>
                        <Menu.Item
                          value="rerun"
                          fontSize="xs"
                          py="1"
                          px="2"
                          onClick={() => handleRerun()}
                        >
                          <HStack gap="1.5" alignItems="center">
                            <RefreshIcon size={12} />
                            <LabelDefault as="span">{t('rerun')}</LabelDefault>
                          </HStack>
                        </Menu.Item>
                        <Menu.Item
                          value="rerun-bypass"
                          fontSize="xs"
                          py="1"
                          px="2"
                          onClick={() =>
                            handleRerun({ bypassSignalGuardrails: true })
                          }
                        >
                          <HStack gap="1.5" alignItems="center">
                            <ShieldBanIcon size={12} />
                            <LabelDefault as="span">
                              {t('rerunBypass')}
                            </LabelDefault>
                          </HStack>
                        </Menu.Item>
                      </Menu.ItemGroup>
                    </Menu.Content>
                  </Menu.Positioner>
                </Menu.Root>
              </ArchivedActionGate>
            )}

            {/* View full signal button */}
            {hasSignal && (
              <Button
                size="xs"
                variant="ghost"
                onClick={handleViewFullSignal}
                onContextMenu={handleViewFullSignal}
              >
                <ViewIcon size={12} />
                View full signal
              </Button>
            )}

            {/* Delete Button */}
            <ArchivedActionGate
              isArchived={isArchived}
              tooltip={archivedActionTooltip}
            >
              <Tooltip title="Delete execution">
                <IconButton
                  variant="ghost"
                  size="xs"
                  onClick={handleDeleteClick}
                  aria-label="Delete execution"
                >
                  <Delete02Icon size={14} />
                </IconButton>
              </Tooltip>
            </ArchivedActionGate>

            <ArchivedActionGate
              isArchived={isArchived}
              tooltip={archivedActionTooltip}
            >
              <Button
                size="xs"
                variant="ghost"
                onClick={handleInvestigateInChat}
              >
                <Message01Icon size={12} />
                Investigate
              </Button>
            </ArchivedActionGate>

            <ArchivedActionGate
              isArchived={isArchived}
              tooltip={archivedActionTooltip}
            >
              <Button
                size="xs"
                variant="ghost"
                onClick={handleCreateFindingInChat}
                aria-label="Generate finding on this execution"
              >
                <CheckListIcon size={12} />
                Generate Finding
              </Button>
            </ArchivedActionGate>
          </HStack>
        </Box>

        {/* Content */}
        <Box flex="1" overflow="hidden">
          <ArtifactsProvider artifacts={history.artifacts ?? []}>
            <ExecutionTraceTabs
              applicationId={applicationId}
              threadId={threadId}
              executionStatus={executionStatus}
              isAttackSkipped={isAttackSkipped}
              activeTab={executionTab}
              onActiveTabChange={setExecutionTab}
              onNavigateToToolCall={handleNavigateToToolCall}
              overview={
                <EntityResolverProvider resolvers={entityResolvers}>
                  {showDetailedView ? (
                    <Box h="full" overflow="hidden">
                      <TraceView
                        ref={detailedViewRef}
                        history={history}
                        maxHeight="100%"
                        status={executionStatus}
                        onStepVisibilityChange={onStepVisibilityChange}
                      />
                    </Box>
                  ) : (
                    <Box h="full" overflow="hidden">
                      <TraceSimplifiedView
                        ref={simplifiedViewRef}
                        signal={
                          hasSignal && history.signal
                            ? {
                                id: history.signal.id,
                                description:
                                  history.signal.description ?? undefined,
                                category: history.signal.category ?? undefined,
                                analysis: history.signal.analysis ?? undefined,
                                stage: history.signal.stage ?? undefined,
                                status: history.signal.status ?? undefined,
                              }
                            : undefined
                        }
                        plan={history.plan}
                        status={executionStatus}
                        metadata={traceMetadata}
                        agentName={agentName ?? undefined}
                        maxHeight="100%"
                        expandedSections={expandedSections}
                        onExpandedSectionsChange={onExpandedSectionsChange}
                        disableDetailedView={hasSkipAttackReason}
                      />
                    </Box>
                  )}
                </EntityResolverProvider>
              }
            />
          </ArtifactsProvider>
        </Box>
      </VStack>

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={({ open }) => setIsDeleteDialogOpen(open)}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        variant="destructive"
        title="Delete Execution"
        description="Are you sure you want to delete this execution? This will permanently delete all execution data including messages and files. This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  );
}
